<template>
  <div class="demo-monitor">
    <TwicImg class="demo-monitor__frame pointer-events-none" :src="monitorFrameSrc" ratio="1310/1020" intrinsic="1310x1020" mode="contain"
      placeholder="none" />
    <div class="demo-monitor__screen relative overflow-hidden bg-black">
      <video
        ref="video"
        class="demo-monitor__video"
        :src="shouldLoad ? videoSrc : undefined"
        muted
        autoplay
        loop
        playsinline
        preload="none"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRuntimeConfig } from '#app'
import { loadGsap } from '@/utils/gsap'

const video = ref<HTMLVideoElement | null>(null)

let isUnmounted = false
let ctx: gsap.Context | null = null

const shouldLoad = ref(false)
const videoSrc = 'https://workbook.7mesh.com/videos/demo_720_.mp4'

const triggerLoadOnce = async () => {
  if (shouldLoad.value) {
    return
  }
  shouldLoad.value = true
  await nextTick()
  if (isUnmounted || !video.value) {
    return
  }
  video.value.play().catch(() => {})
}

onMounted(async () => {
  await nextTick()
  if (!video.value) {
    return
  }

  const { gsap, ScrollTrigger } = await loadGsap()
  ctx = gsap.context(() => {
    ScrollTrigger.create({
      trigger: document.documentElement,
      start: 'top top',
      end: '+=1',
      onUpdate: (self) => {
        if (self.scroll() > 0) {
          triggerLoadOnce()
          self.kill()
        }
      }
    })
    ScrollTrigger.create({
      trigger: video.value,
      start: 'top 120%',
      once: true,
      onEnter: () => triggerLoadOnce()
    })
  }, video.value)
})

onBeforeUnmount(() => {
  isUnmounted = true
  if (video.value) {
    video.value.pause()
    video.value.removeAttribute('src')
    video.value.load()
  }
  ctx?.revert()
  ctx = null
})

const config = useRuntimeConfig()
const monitorFrameSrc = computed(() =>
  '/images/intro/monitor-front.png' + '?deployment=' + config.public.appVersion
)
</script>

<style scoped>
.demo-monitor {
  width: 100%;
  max-width: 720px;
  position: relative;
}

.demo-monitor__frame {
  width: 100%;
  height: auto;
  position: relative;
  z-index: 10;
}

.demo-monitor__screen {
  position: absolute;
  top: 3%;
  left: 1%;
  width: 97%;
  height: 71%;
  overflow: hidden;
  border-radius: 1.5%;
  z-index: 0;
}

.demo-monitor__video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
